import { useCallback, useState } from 'react'
import { useSearch } from '@tanstack/react-router'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { BellRing, CheckCheck, ChevronLeft, Lock, Settings, Wifi, WifiOff } from 'lucide-react'
import { notiApi, streamUrl, type Channel, type FeedResponse, type PreferenceWorkflow } from '../lib/api-noti'
import { FEED_POLL_FAST, FEED_POLL_SLOW, resolveDeepLink } from '../lib/config'
import { useEventSource } from '../lib/useEventSource'
import { Toggle } from '../components/Toggle'

// Standalone inbox for the APK WebView (and the localhost demo client).
// No portal chrome — this page must feel like a screen inside the mobile app.
// URL contract: /inbox?subscriber=CIF-xxxx
export function InboxPage() {
  const { subscriber } = useSearch({ from: '/inbox' })
  const [view, setView] = useState<'feed' | 'prefs'>('feed')

  if (!subscriber) {
    return (
      <div className="inbox-shell">
        <header className="inbox-head">
          <BellRing size={20} aria-hidden />
          <h1>การแจ้งเตือน</h1>
        </header>
        <div className="inbox-empty">
          <p className="muted">
            ต้องระบุผู้ใช้: <code>/inbox?subscriber=CIF-…</code>
          </p>
          <p className="muted" style={{ fontSize: '0.85rem' }}>
            (แอปเป็นผู้เติม query นี้หลัง login — ดูคู่มือทีม Mobile/APK ในแท็บ Docs)
          </p>
        </div>
      </div>
    )
  }

  return view === 'feed' ? (
    <InboxFeed subscriber={subscriber} onPrefs={() => setView('prefs')} />
  ) : (
    <InboxPrefs subscriber={subscriber} onBack={() => setView('feed')} />
  )
}

function InboxFeed({ subscriber, onPrefs }: { subscriber: string; onPrefs: () => void }) {
  const qc = useQueryClient()
  const feedKey = ['noti', 'feed', subscriber] as const

  const invalidate = useCallback(() => {
    qc.invalidateQueries({ queryKey: ['noti', 'feed', subscriber] })
  }, [qc, subscriber])

  const sseConnected = useEventSource(streamUrl.subscriber(subscriber), invalidate)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: feedKey,
    queryFn: () => notiApi.feed(subscriber, 1, 50),
    refetchInterval: sseConnected ? FEED_POLL_SLOW : FEED_POLL_FAST,
  })

  const markRead = useMutation({
    mutationFn: (id: number) => notiApi.markRead(subscriber, id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: feedKey })
      const prev = qc.getQueryData<FeedResponse>(feedKey)
      qc.setQueryData<FeedResponse>(feedKey, (old) =>
        old
          ? {
              ...old,
              unreadCount: Math.max(0, old.unreadCount - 1),
              data: old.data.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
            }
          : old,
      )
      return { prev }
    },
    onError: (_e, _id, ctx) => ctx?.prev && qc.setQueryData(feedKey, ctx.prev),
    onSettled: invalidate,
  })

  const markAll = useMutation({ mutationFn: () => notiApi.markAllRead(subscriber), onSettled: invalidate })

  const unread = data?.unreadCount ?? 0

  return (
    <div className="inbox-shell">
      <header className="inbox-head">
        <BellRing size={20} aria-hidden />
        <h1>การแจ้งเตือน</h1>
        {unread > 0 && <span className="bell-badge static">{unread > 99 ? '99+' : unread}</span>}
        <span className="inbox-conn" title={sseConnected ? 'เชื่อมต่อสด' : 'โหมด polling'}>
          {sseConnected ? <Wifi size={16} aria-hidden /> : <WifiOff size={16} aria-hidden />}
        </span>
        <button className="inbox-icon-btn" onClick={onPrefs} aria-label="ตั้งค่าการแจ้งเตือน">
          <Settings size={20} aria-hidden />
        </button>
      </header>

      <div className="inbox-sub muted">
        {subscriber}
        <button
          className="link-btn"
          onClick={() => markAll.mutate()}
          disabled={markAll.isPending || unread === 0}
        >
          <CheckCheck size={14} aria-hidden /> อ่านทั้งหมด
        </button>
      </div>

      {isLoading ? (
        <div className="inbox-empty muted">โหลด…</div>
      ) : isError ? (
        <div className="inbox-empty error">{(error as Error).message}</div>
      ) : !data || data.data.length === 0 ? (
        <div className="inbox-empty muted">ยังไม่มีการแจ้งเตือน</div>
      ) : (
        <ul className="inbox-list">
          {data.data.map((n) => (
            <li
              key={n.id}
              className={`inbox-item ${n.readAt ? '' : 'unread'}`}
              onClick={() => !n.readAt && markRead.mutate(n.id)}
            >
              <div className="t">{n.title}</div>
              <div className="b">{n.body}</div>
              <div className="m muted">{new Date(n.createdAt).toLocaleString('th-TH')}</div>
              {n.action?.label && n.action.deepLink && (
                <a className="inbox-action" href={resolveDeepLink(String(n.action.deepLink), subscriber)}>
                  {String(n.action.label)}
                </a>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

function InboxPrefs({ subscriber, onBack }: { subscriber: string; onBack: () => void }) {
  const qc = useQueryClient()
  const prefKey = ['noti', 'prefs', subscriber] as const

  const { data, isLoading, isError, error } = useQuery({
    queryKey: prefKey,
    queryFn: () => notiApi.preferences(subscriber),
  })

  // หนึ่งสวิตช์ต่อ workflow — เปิด/ปิดมีผลทุกช่องทาง (ในแอป มือถือ อีเมล) พร้อมกัน
  const mut = useMutation({
    mutationFn: (v: { workflowKey: string; channels: Channel[]; enabled: boolean }) =>
      Promise.all(v.channels.map((ch) => notiApi.putPreference(subscriber, v.workflowKey, ch, v.enabled))),
    onMutate: async (v) => {
      await qc.cancelQueries({ queryKey: prefKey })
      const prev = qc.getQueryData<{ data: PreferenceWorkflow[] }>(prefKey)
      qc.setQueryData<{ data: PreferenceWorkflow[] }>(prefKey, (old) =>
        old
          ? {
              data: old.data.map((w) =>
                w.workflowKey === v.workflowKey
                  ? { ...w, channels: Object.fromEntries(v.channels.map((ch) => [ch, v.enabled])) }
                  : w,
              ),
            }
          : old,
      )
      return { prev }
    },
    onError: (_e, _v, ctx) => ctx?.prev && qc.setQueryData(prefKey, ctx.prev),
    onSettled: () => qc.invalidateQueries({ queryKey: prefKey }),
  })

  return (
    <div className="inbox-shell">
      <header className="inbox-head">
        <button className="inbox-icon-btn" onClick={onBack} aria-label="ย้อนกลับ">
          <ChevronLeft size={22} aria-hidden />
        </button>
        <h1>ตั้งค่าการแจ้งเตือน</h1>
      </header>
      <div className="inbox-sub muted">{subscriber}</div>

      {isLoading ? (
        <div className="inbox-empty muted">โหลด…</div>
      ) : isError ? (
        <div className="inbox-empty error">{(error as Error).message}</div>
      ) : (
        <ul className="prefs-list">
          {(data?.data ?? []).map((w) => {
            const enabled = Object.values(w.channels).some(Boolean)
            return (
              <li key={w.workflowKey} className="prefs-item">
                <label className="prefs-row">
                  <span className="prefs-name">
                    {w.name}
                    {w.critical && (
                      <span className="prefs-lock muted" title="แจ้งเตือนสำคัญ — ปิดไม่ได้">
                        <Lock size={13} aria-hidden /> สำคัญ
                      </span>
                    )}
                  </span>
                  <Toggle
                    checked={w.critical || enabled}
                    disabled={w.critical || mut.isPending}
                    onChange={(next) => mut.mutate({ workflowKey: w.workflowKey, channels: Object.keys(w.channels) as Channel[], enabled: next })}
                    label={w.name}
                  />
                </label>
              </li>
            )
          })}
        </ul>
      )}
      <p className="muted" style={{ padding: '0 1rem 1rem', fontSize: '0.8rem' }}>
        ปิดรายการใด ระบบจะงดแจ้งเตือนเรื่องนั้นทุกช่องทาง (ในแอป · มือถือ · อีเมล) —
        แจ้งเตือนที่สำคัญ (มีกุญแจ) ส่งเสมอ
      </p>
    </div>
  )
}
